import { useEffect } from 'react'
import { graphql, Link, navigate } from 'gatsby'
import Layout from 'components/Layout/Layout'
import SEO from 'components/SEO'
import { GatsbyImage } from 'gatsby-plugin-image'
import { imageHelper } from 'utils/imageUtils'
import { FacebookShareButton, FacebookIcon, EmailShareButton, EmailIcon, TwitterShareButton, TwitterIcon } from 'react-share'
import { useLocation } from '@reach/router'
import { postTitleToUrl } from 'utils/urlUtils'
import { textFromHtml } from 'utils/textUtils'
import {
  OuterContainer,
  InnerContainer,
  PostDate,
  PostDateLarge,
  TitleRow,
  Title,
  Subtitle,
  Caption,
  BodyText,
  NextPreviousContainer,
  NextArrow,
  PreviousArrow,
  // TagRow,
  // Tag,
  DesktopShareContainer,
  MobileShareContainer,
  RelatedPostsContainer,
  RelatedPost,
  RelatedPostTitle,
  RelatedPostSubtitle
} from 'styles/postEntryStyles'

const PostEntry = ({ data }) => {
  const postData = data.directus.currentPost
  const location = useLocation()
  const currentTags = postData.tags.map(tag => tag.tag_id.name)
  const currentDate = new Date(postData.date_created).toLocaleString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
  const dateParts = currentDate.split(' ')

  const relatedPosts = data.directus.allPosts
    .filter(post => post.id !== postData.id)
    .filter(post =>
      post.tags
        .map(tag => tag.tag_id.name)
        .some(tag => currentTags.includes(tag))
    )
    .slice(0, 3)

  const currentPostIndex = data.directus.allPosts.findIndex(post => post.title === postData.title)

  useEffect(() => {
    const onKeyDown = e => {
      if (e.key === 'ArrowLeft' && currentPostIndex > 0) {
        navigate(postTitleToUrl(data.directus.allPosts[currentPostIndex - 1].title))
      }
      else if (e.key === 'ArrowRight' && currentPostIndex >= 0 && currentPostIndex < data.directus.allPosts.length - 1) {
        navigate(postTitleToUrl(data.directus.allPosts[currentPostIndex + 1].title))
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [currentPostIndex, data.directus.allPosts])

  const ShareIcons = () =>
    <>
      <FacebookShareButton url={location.href} >
        <FacebookIcon size={24} bgStyle={{ fill: '#e9e9e9' }} iconFillColor='#585858' />
      </FacebookShareButton>
      <TwitterShareButton url={location.href} >
        <TwitterIcon size={24} bgStyle={{ fill: '#e9e9e9' }} iconFillColor='#585858' />
      </TwitterShareButton>
      <EmailShareButton url={location.href} subject={postData.title} >
        <EmailIcon size={24} bgStyle={{ fill: '#e9e9e9' }} iconFillColor='#585858' />
      </EmailShareButton>
    </>

  return (
    <Layout>
      <OuterContainer>
        <InnerContainer>
          <PostDate>{dateParts[0]}<PostDateLarge>{` ${dateParts[1]} ${dateParts[2]}`}</PostDateLarge></PostDate>
          <TitleRow>
            <Title>{postData.title}</Title>
            <DesktopShareContainer>
              <ShareIcons />
            </DesktopShareContainer>
          </TitleRow>
          <Subtitle dangerouslySetInnerHTML={{ __html: postData.subtitle }} />
          <MobileShareContainer>
            <ShareIcons />
          </MobileShareContainer>

          <GatsbyImage image={imageHelper(postData.main_image)} alt={textFromHtml(postData.image_caption)} />
          <Caption dangerouslySetInnerHTML={{ __html: postData.image_caption }} />
          <BodyText dangerouslySetInnerHTML={{ __html: postData.body_text }} />
          <NextPreviousContainer>
            {currentPostIndex > 0 ?
              <Link to={postTitleToUrl(data.directus.allPosts[currentPostIndex - 1].title)}>
                <PreviousArrow>{'<'}</PreviousArrow>
                PREVIOUS
              </Link>
              :
              <div />
            }
            {currentPostIndex >= 0 && currentPostIndex < data.directus.allPosts.length - 1 ?
              <Link to={postTitleToUrl(data.directus.allPosts[currentPostIndex + 1].title)}>
                NEXT
                <NextArrow>{'>'}</NextArrow>
              </Link>
              :
              <div />
            }
          </NextPreviousContainer>
          {/* // TODO: for future use.
          {postData.tags?.length > 0 &&
            <TagRow>
              TAGGED:
              {postData.tags.map(tag =>
                <Link to={`/posts?tag=${tag.tag_id.name}`}>
                  <Tag>{tag.tag_id.name}</Tag>
                </Link>
              )}
            </TagRow>
          }
          */}
          <RelatedPostsContainer>
            {relatedPosts.map(post =>
              <RelatedPost key={post.id} to={postTitleToUrl(post.title)}>
                <GatsbyImage image={imageHelper(post.main_image)} alt={textFromHtml(post.image_caption)} />
                <div>
                  <RelatedPostTitle>{post.title}</RelatedPostTitle>
                  <RelatedPostSubtitle dangerouslySetInnerHTML={{ __html: post.subtitle }} />
                </div>
              </RelatedPost>
            )}
          </RelatedPostsContainer>
        </InnerContainer>
      </OuterContainer>
    </Layout>
  )
}

export default PostEntry

export const Head = ({ data }) =>
  <SEO
    title={data.directus.currentPost.title}
    description={textFromHtml(data.directus.currentPost.subtitle)}
    image={data.directus.currentPost.main_image}
  />


export const query = graphql`
  query($id: ID!) {
    directus {
      currentPost: post_by_id(id: $id) {
        id
        title
        subtitle
        body_text
        main_image {
          id
          width
          height
          imageFile {
            publicURL
            childImageSharp {
              gatsbyImageData
            }
          }
        }
        image_caption
        date_created
        tags {
          tag_id {
            name
          }
        }
      }
      allPosts: post(filter: {status: {_eq: "published"}}, sort: "-date_created") {
        id
        title
        subtitle
        image_caption
        main_image {
          id
          imageFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
        tags {
          tag_id {
            name
          }
        }
      }
    }
  }
`
